// ============================================================================
// kepNezo.js — teljes képernyős képnézegető.
//
// A csatolt képre koppintva megnyílik nagyban, és onnan LETÖLTHETŐ, illetve
// MEGOSZTHATÓ (ahol a rendszer támogatja).
//
// Miért külön nézegető, és miért nem egyszerűen új lapon nyitjuk meg?
// A kép `blob:` címen érkezik (lásd kepBetolto.js), ami CSAK ezen a lapon
// érvényes. Új lapra átvíve vagy megosztva a cím semmit nem jelentene.
// Ezért a letöltést és a megosztást magával az adattal (Blob) végezzük.
//
// A megosztás a Web Share API-val megy, fájllal együtt. Ezt a mobilok
// nagy része támogatja; asztali böngészőkben jellemzően nem, ezért a gomb
// CSAK ott jelenik meg, ahol a rendszer ténylegesen tudja - egy működésképtelen
// gomb rosszabb, mint a hiánya.
// ============================================================================

import { t } from "./i18n.js";
import { log } from "./log.js";

let nyitottNezo = null;

/** Támogatja-e a böngésző a fájlok megosztását? */
export function tudMegosztani(blob, nav = typeof navigator !== "undefined" ? navigator : null) {
  try {
    if (!nav || typeof nav.share !== "function" || typeof nav.canShare !== "function") return false;
    if (typeof File !== "function") return false;
    const f = new File([blob], "kep.jpg", { type: blob.type || "image/jpeg" });
    return nav.canShare({ files: [f] });
  } catch (e) {
    return false;
  }
}

/**
 * Mobil / érintőkijelzős eszköz-e (telefon, tablet)? Az asztali gépen (Windows, Mac, Linux; böngészőben és telepített alkalmazásként is) a
 * Letöltés gomb KÖZVETLENÜL ment (v155, tagi jelzés Windowson, telepített ablakban: a rendszer megosztási panelje nem jelent meg, a gomb
 * "nem csinált semmit"); a natív megosztás csak mobilon az elsődleges út, ahol a "Kép mentése" ott megbízható.
 */
export function mobilEszkoz(nav = typeof navigator !== "undefined" ? navigator : null) {
  try {
    if (!nav) return false;
    if (nav.userAgentData && typeof nav.userAgentData.mobile === "boolean") return nav.userAgentData.mobile;
    const ua = String(nav.userAgent || "");
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
    // iPadOS "Macintosh"-ként jelentkezik, de érintőkijelzős
    return nav.platform === "MacIntel" && Number(nav.maxTouchPoints) > 1;
  } catch (e) {
    return false;
  }
}

/**
 * A MÁR KIRAJZOLT <img> elemből vásznon (canvas) keresztül nyeri ki a képet,
 * HÁLÓZATI KÉRÉS NÉLKÜL (2026-10-03, tagi jelzésre: a megosztás-alapú
 * letöltés Androidon "Failed to fetch" hibával elhasalt).
 *
 * MIÉRT EZ AZ ELSŐDLEGES ÚT: a kép ekkor MÁR a felhasználó képernyőjén
 * látszik - vagyis a böngésző már sikeresen megszerezte és dekódolta. A
 * "Letöltés" gomb korábban ÚJRA lekérte a képet hálózaton (`fetch(cim)`),
 * ami - a tagi visszajelzés szerint - legalább egy Android-böngészőn
 * megbízhatatlan (a konkrét okot élő eszköz nélkül nem sikerült
 * beazonosítani - lehet a Storage-vödör CORS-beállítása, lásd
 * docs/UZEMELTETES.md "Storage CORS", vagy a blob: cím időközbeni
 * érvénytelenné válása). A vászon-alapú kinyerés ezt a hálózati lépést
 * TELJESEN megkerüli.
 *
 * KORLÁT: ha a kép végül a NYILVÁNOS (nem hitelesített) tartalék címről
 * jelenik meg (lásd kepBetolto.js - ez akkor fordul elő, ha a hitelesített
 * letöltés a CORS-beállítás hiánya miatt elbukott), a vászon "szennyezetté"
 * válik, és a kiolvasás biztonsági hibával elutasul - ilyenkor a kód a
 * RÉGI, hálózati fetch() útra esik vissza, ami pontosan ugyanazzal a
 * CORS-hiánnyal fog elhasalni. Ez a helyzet KÓDBÓL nem javítható - a
 * Storage-vödör CORS-beállítása a tényleges megoldás.
 */
function blobVasznonKeresztul(imgEl) {
  return new Promise((resolve, reject) => {
    try {
      if (!imgEl || !imgEl.complete || !imgEl.naturalWidth) {
        reject(new Error("a kép még nincs betöltve"));
        return;
      }
      const vaszon = document.createElement("canvas");
      vaszon.width = imgEl.naturalWidth;
      vaszon.height = imgEl.naturalHeight;
      vaszon.getContext("2d").drawImage(imgEl, 0, 0);
      vaszon.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("a vászon üres adatot adott"));
      }, "image/jpeg", 0.95);
    } catch (err) {
      // Jellemzően "szennyezett vászon" biztonsági hiba - lásd a fenti
      // KORLÁT megjegyzést.
      reject(err);
    }
  });
}

/**
 * A kép nyers adata. ELSŐKÉNT a már kirajzolt <img> elemből, vászonnal -
 * hálózati kérés nélkül (lásd blobVasznonKeresztul). Csak akkor esik vissza
 * hálózati fetch()-re, ha ez nem lehetséges (pl. az elem még nem létezik
 * ezen a hívási ponton, vagy "szennyezett" a vászon).
 */
async function adatLekeres(cim, imgEl) {
  if (imgEl) {
    try {
      return await blobVasznonKeresztul(imgEl);
    } catch (err) {
      log.debug("[kép] vászon-alapú kiolvasás nem sikerült, hálózati letöltésre esünk vissza:", err && err.message);
    }
  }
  const valasz = await fetch(cim);
  if (!valasz.ok) throw new Error(`a kép nem tölthető le (${valasz.status})`);
  return valasz.blob();
}

/**
 * A Blob-ot data: URL-lé alakítja.
 *
 * MIÉRT KELL: a letöltés gombja korábban `URL.createObjectURL()`-lel
 * (blob: cím) készített anchor-t. Ez asztali böngészőkön működik, de
 * Android Chrome-on NEM: a tényleges fájlmentést a rendszer letöltés-
 * kezelője végzi, ami csak a lapon KÍVÜLRŐL is elérhető címet tud
 * fogadni (http(s):, data:) - a blob: cím csak az adott lap memóriájában
 * él, ezért Androidon a letöltés némán elhasal, miközben a kép
 * megjelenítése (ugyanaz a blob:) továbbra is működik.
 *
 * A data: URL ezt megkerüli, mert önmagában hordozza az adatot - ez
 * mindkét platformon megbízhatóan letölthető.
 */
function blobDataUrlKent(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("olvasási hiba"));
    reader.readAsDataURL(blob);
  });
}

function fajlnev() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `lineage0-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.jpg`;
}

/**
 * A nézegető megnyitása.
 *
 * @param {string} cim a megjelenítendő kép címe (blob: vagy https:)
 * @param {string} uiLang
 */
export function nyissMegKepet(cim, uiLang) {
  if (!cim) return null;
  zarjBeKepet();

  const hatter = document.createElement("div");
  hatter.className = "kepnezo";

  const kep = document.createElement("img");
  kep.className = "kepnezo-kep";
  kep.src = cim;
  kep.alt = t(uiLang, "image.alt");
  hatter.appendChild(kep);

  const sav = document.createElement("div");
  sav.className = "kepnezo-sav";

  // Mindig látható segítség (2026-10-03, tagi jelzésre: a megosztás-alapú
  // mentés Androidon sem működött megbízhatóan, az ok még nem azonosított).
  // EZ A VÉGSŐ, API-FÜGGETLEN tartalék: a kép itt, a nézegetőben teljes
  // méretben megjelenik, és a hosszan nyomva előhívható "Kép mentése"
  // rendszer-menü szinte minden mobil böngészőn működik, FÜGGETLENÜL attól,
  // hogy a share/letöltés API-k az adott eszközön/böngészőben épp
  // megbízhatóak-e. Ez nem helyettesíti a gombot, csak biztosítékul szolgál,
  // ha a gomb néma marad.
  const sugo = document.createElement("div");
  sugo.className = "kepnezo-sugo";
  sugo.textContent = t(uiLang, "image.longPressHint");
  sugo.hidden = !mobilEszkoz(); // a hosszú nyomás csak érintőkijelzőn értelmezhető; asztali gépen a gomb közvetlenül ment
  hatter.appendChild(sugo);

  // Látható hibajelzés (2026-10-03, tagi jelzésre): korábban egy sikertelen
  // mentés csak a fejlesztői konzolra írt - egy telefonon ezt a tag soha
  // nem látja, így "nem csinál semmit" élményt kap, és nekünk sincs
  // támpontunk, MIÉRT nem sikerült. Innentől a hiba (üres sorként maradva,
  // ha nincs rá szükség) a nézegetőben is megjelenik.
  const hibaSor = document.createElement("div");
  hibaSor.className = "kepnezo-hiba";
  hibaSor.hidden = true;
  hatter.appendChild(hibaSor);

  const gomb = (szoveg, cim2, onclick) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "kepnezo-gomb";
    b.textContent = szoveg;
    b.title = cim2;
    b.addEventListener("click", (e) => {
      // A háttérre kattintás zárja a nézegetőt - a gombokra kattintás NEM.
      e.stopPropagation();
      onclick(b);
    });
    return b;
  };

  // --- Letöltés (mentés)
  //
  // VALÓS HIBÁBÓL TANULVA (2026-10-03, tagi jelzésre): asztali gépen ez a
  // gomb az <a download> + data: URL trükkel megbízhatóan működött (lásd
  // lentebb, és a blobDataUrlKent() megjegyzését az Android-specifikus
  // korábbi javításról). MOBILON VISZONT - elsősorban iOS-en (Safari és a
  // rajta futó összes böngésző, mert mindegyik a WebKit motort használja) -
  // a `download` attribútumot a rendszer EGYSZERŰEN FIGYELMEN KÍVÜL HAGYJA:
  // a kattintás a data: URL-t egyszerűen MEGNYITJA (navigáció), nem menti
  // el, és eközben SEMMILYEN hibát nem dob - a gomb a felhasználó szemében
  // "nem csinál semmit", miközben a kódban minden rendben lefutott.
  //
  // JAVÍTÁS: ha a rendszer támogatja a natív megosztást (ez szinte minden
  // mai mobil böngészőre igaz - lásd tudMegosztani()), ELSŐKÉNT azt
  // használjuk: a megosztási felületen a "Kép mentése" / "Save Image"
  // lehetőség ez a telefonok natív, megbízható módja a fényképtárba
  // mentésnek. Az <a download> trükk csak TARTALÉKKÉNT marad, arra az
  // esetre, ha a megosztás nem elérhető (jellemzően asztali gépen) vagy
  // elhasal.
  sav.appendChild(
    gomb(t(uiLang, "image.download"), t(uiLang, "image.download"), async (b) => {
      b.disabled = true;
      hibaSor.hidden = true;
      hibaSor.classList.remove("kepnezo-ok");
      try {
        const blob = await adatLekeres(cim, kep);
        if (mobilEszkoz() && tudMegosztani(blob)) {
          // MOBILON a natív megosztás az elsődleges ("Kép mentése" ott megbízható).
          const f = new File([blob], fajlnev(), { type: blob.type || "image/jpeg" });
          await navigator.share({ files: [f] });
        } else {
          // ASZTALI gépen (és ha nincs megosztás) közvetlen letöltés. Először blob: cím (asztali böngészőn és telepített ablakban
          // megbízható, nagy képnél sem korlátos); ha nem érhető el, data: URL (Android Chrome-on a blob: letöltés némán elhasal -
          // lásd blobDataUrlKent() megjegyzését).
          let url = null;
          let blobCim = false;
          try {
            if (typeof URL !== "undefined" && typeof URL.createObjectURL === "function") { url = URL.createObjectURL(blob); blobCim = true; }
          } catch (e) { url = null; }
          if (!url) url = await blobDataUrlKent(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fajlnev();
          document.body.appendChild(a);
          a.click();
          a.remove();
          // A blob: címet a letöltés elindulása után szabadítjuk fel (a data: URL nem lap-memóriára mutat).
          if (blobCim) setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* nem kritikus */ } }, 60000);
          // Látható visszajelzés: a gomb eddig "nem csinált semmit" a telepített ablakban.
          if (!mobilEszkoz()) {
            hibaSor.textContent = t(uiLang, "image.saveStarted");
            hibaSor.hidden = false;
            hibaSor.classList.add("kepnezo-ok");
          }
        }
      } catch (err) {
        // A felhasználó megszakíthatja a megosztást - ez nem hiba.
        if (!err || err.name !== "AbortError") {
          log.error("[kép] letöltés hiba:", err);
          // LÁTHATÓ jelzés (lásd a hibaSor fenti megjegyzését) - a pontos
          // hibaüzenet (err.message) segít beazonosítani, mi ment félre
          // azon az eszközön, ahol a gomb néma marad.
          hibaSor.textContent = `${t(uiLang, "image.saveFailed")} (${(err && err.message) || err})`;
          hibaSor.hidden = false;
        }
      }
      b.disabled = false;
    })
  );

  // KÜLÖN "Megosztás" gomb MÁR NINCS: a "Letöltés" gomb (feljebb) mobilon
  // MOST MÁR MAGA is a natív megosztási felületet nyitja meg (lásd ott a
  // megjegyzést) - két, látszatra különböző, valójában azonos művelet
  // gomb csak zavaró lett volna.

  sav.appendChild(
    gomb(t(uiLang, "image.close"), t(uiLang, "image.close"), () => zarjBeKepet())
  );
  hatter.appendChild(sav);

  // A háttérre koppintás zár - ez a megszokott viselkedés.
  hatter.addEventListener("click", () => zarjBeKepet());

  const eszkez = (e) => {
    if (e.key === "Escape") zarjBeKepet();
  };
  document.addEventListener("keydown", eszkez);

  document.body.appendChild(hatter);
  nyitottNezo = { hatter, eszkez };
  return hatter;
}

/** A nézegető bezárása. Többször hívható. */
export function zarjBeKepet() {
  if (!nyitottNezo) return;
  document.removeEventListener("keydown", nyitottNezo.eszkez);
  nyitottNezo.hatter.remove();
  nyitottNezo = null;
}

/** Csak a teszteknek: nyitva van-e a nézegető? */
export function nyitvaVan() {
  return nyitottNezo !== null;
}
